<script lang="ts" setup>
import { getPosPrinter } from '@/utils/common';
import { pb } from "@/utils/PocketBaseAdapter";

// Interfaces
interface Emit {
  (e: "closeDialog", value: boolean): void;
  (e: "print", value: string, printer: string): void;
}

interface Props {
  isVisible?: boolean;
  isAdmin?: boolean;
  emailed?: string;
  printed?: string;
  haveEditAccess?: boolean;
}

// Props and Emits
const props = defineProps<Props>();
const emit = defineEmits<Emit>();

// References
const email = ref<string>("");
const refReceiptVForm = ref<any>();
const emailedDate = ref<string>(props.emailed || "");
const printedDate = ref<string>(props.printed || "");
const printer = ref<string>('');
const printerList = ref<Array<{ id: string; name: string, type: string }>>([]);

// Methods
const print = async (type: string) => {
  const { valid } = await refReceiptVForm.value?.validate();
  if (valid) emit("print", type, printer.value);
};

const closeDialog = () => emit("closeDialog", false);

// Watch Props
watch(
  () => props.isVisible,
  (isVisible) => {
    if (!isVisible) {
      emailedDate.value = "";
      printedDate.value = "";
      email.value = "";
    } else {
      emailedDate.value = props.emailed || "";
      printedDate.value = props.printed || "";
    }
  },
  { immediate: true }
);

onMounted(async() => {
  printer.value = await getPosPrinter();
  const printers = await pb.collection("printers").getFullList({ filter: 'printerType!="Label"', requestKey: null });
  printerList.value = printers.map((item) => ({
    id: item.id,
    name: item.name,
    type: item.printerType
  }));
})
</script>

<template>
  <VDialog
    persistent
    :model-value="props.isVisible"
    no-click-animation
    width="600"
  >
    <VCard flat>
      <!-- Header -->
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor"
      >
        <VCardTitle>Print Ticket</VCardTitle>
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          @click="closeDialog"
        ></VBtn>
      </div>

      <!-- Body -->
      <span class ="pl-4 pt-2">Ticket will be emailed to the booking owner.</span>
       
      <VCardText>
        <VResponsive class="mx-auto">
          <VForm ref="refReceiptVForm" class="mt-2">
            <VRow v-if="haveEditAccess">
              <VCol cols="12" md="6">
                <DateInput
                  v-model="emailedDate"
                  showTime
                  readonly
                  label="Emailed Date"
                  placeholder="Emailed Date"
                  format="DD-MM-YYYY HH:mmA"
                />
              </VCol>
              <VCol cols="12" md="6">
                <DateInput
                  v-model="printedDate"
                  showTime
                  label="Printed Date"
                  placeholder="Printed Date"
                  format="DD-MM-YYYY HH:mmA"
                  readonly
                />
              </VCol>
              <VCol cols="12" v-if="isAdmin">
                <v-select
                  v-if="printerList.length > 1"
                  v-model="printer"
                  :items="printerList"
                  item-title="name"
                  item-value="id"
                  label="Printer"
                  class="mt-4"
                />
              </VCol>
            </VRow>
          </VForm>
        </VResponsive>
      </VCardText>
      <!-- Actions -->
       <VCardActions class="d-flex justify-end flex-wrap gap-4">
        <VBtn color="primary" variant="flat" @click="print('EMAIL')">Email Ticket</VBtn>
        <VBtn color="primary" variant="flat" v-if="isAdmin" @click="print('PRINT')">
          Print Ticket
        </VBtn>
        <VBtn color="error" variant="flat" @click="closeDialog">Close</VBtn>
       </VCardActions>
    </VCard>
  </VDialog>
</template>
